import React, { useEffect, useMemo, useState } from "react";
import { Button, Input, Select, Spin, Table, Tabs, Tag, message } from "antd";
import type { ColumnsType } from "antd/es/table";
import AppModal from "../../modals/AppModal";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import {
  fetchLinkableEntities,
  resetLinkableEntities,
  LinkableEntityType,
  LinkStatus,
} from "./memo.slice";
import "./linkedMediaModalForMemo.css";
import GenericTable from "../../GeneralTable/GenericTable";

type Props = {
  open: boolean;
  memo: {
    id: string;
    title?: string;
    content?: string;
    preview?: string;
  } | null;
  groupOptions: { label: string; value: string }[];
  onClose: () => void;

  /**
   * OPTIONAL: kur ta kesh endpoint-in per link/unlink,
   * e lidhim ketu. Per moment UI shfaqet.
   */
  onToggleLink?: (entityId: string, nextLinked: boolean) => Promise<void>;
};

const { Search } = Input;

const LinkedMediaModalForMemo: React.FC<Props> = ({
  open,
  memo,
  groupOptions,
  onClose,
  onToggleLink,
}) => {
  const dispatch = useAppDispatch();

  const { linkableItems, linkableTotal, linkableLoading, linkableError } =
    useAppSelector((s: any) => s.memo);

  // top controls (as per figma)
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
  const [memoTitle] = useState<string>(""); // memo is selected from card
  const [memoDescription] = useState<string>(""); // for now read-only visual

  const [debouncedSearch, setDebouncedSearch] = useState("");

  // filters
  const [search, setSearch] = useState("");
  const [entityType, setEntityType] = useState<LinkableEntityType | "">("");
  const [linkStatus, setLinkStatus] = useState<LinkStatus>("all");

  // pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const skip = useMemo(
    () => Math.max(0, (page - 1) * pageSize),
    [page, pageSize]
  );

  const [togglingId, setTogglingId] = useState<string | null>(null);

  // tabs: Linked / Unlinked
  // const tabItems = useMemo(
  //   () => [
  //     { key: "linked", label: "Link" },
  //     { key: "unlinked", label: "Unlinked" },
  //   ],
  //   []
  // );

  // auto-set linkStatus when switching tabs (figma behavior)
  // const handleTabChange = (key: string) => {
  //   setPage(1);
  //   setLinkStatus(key === "unlinked" ? "unlinked" : "linked");
  // };
  useEffect(() => {
    if (!open) return;

    const t = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 400);

    return () => clearTimeout(t);
  }, [search, open]);

  const runFetch = () => {
    if (!memo?.id) return;
    dispatch(
      fetchLinkableEntities({
        memoId: memo.id,
        entity_type: entityType || undefined,
        // search: search || undefined,
        search: debouncedSearch || undefined,
        link_status: linkStatus,
        skip,
        limit: pageSize,
      })
    );
  };

  useEffect(() => {
    if (!open || !memo?.id) return;

    // default state on open
    setSearch("");
    setEntityType("");
    setLinkStatus("all");
    setPage(1);
    setPageSize(10);
    setSelectedGroupIds([]);

    runFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, memo?.id]);

  useEffect(() => {
    if (!open || !memo?.id) return;
    runFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // }, [open, memo?.id, entityType, linkStatus, skip, pageSize]);
  }, [open, memo?.id, entityType, linkStatus, debouncedSearch, skip, pageSize]);

  useEffect(() => {
    if (!open) return;
    if (linkableError) message.error(linkableError);
  }, [open, linkableError]);

  const close = () => {
    dispatch(resetLinkableEntities());
    onClose();
  };

  const typeOptions = [
    { label: "Document", value: "document" },
    { label: "Highlight", value: "highlight" },
    { label: "Code", value: "code" },
    { label: "Descriptor", value: "descriptor" },
  ];

  const linkStatusOptions = [
    { label: "Linked", value: "linked" },
    { label: "Unlinked", value: "unlinked" },
  ];

  const columns: ColumnsType<any> = [
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      width: 140,
      render: (v) => <Tag>{String(v)}</Tag>,
    },
    {
      title: "Item",
      dataIndex: "item",
      key: "item",
      render: (_: any, row: any) => row?.item || row?.name || "—",
    },
    // {
    //   title: "User",
    //   dataIndex: "user",
    //   key: "user",
    //   width: 160,
    //   render: (v) => v || "—",
    // },
    {
      title: "",
      key: "action",
      width: 120,
      render: (_: any, row: any) => {
        const isLinked = !!row?.is_linked;
        const btnText = isLinked ? "Unlink" : "Link";
        const isLoading = togglingId === row.id;

        return (
          <Button
            type="primary"
            ghost={isLinked}
            loading={isLoading}
            disabled={!!togglingId && !isLoading}
            onClick={async () => {
              if (!onToggleLink) {
                message.info("Link/Unlink endpoint is not connected yet.");
                return;
              }
              try {
                setTogglingId(row.id);
                await onToggleLink(row.id, !isLinked);
                runFetch();
              } catch (e: any) {
                message.error(e?.message || "Failed to update link status");
              } finally {
                setTogglingId(null);
              }
            }}
          >
            {btnText}
          </Button>
        );
      },
    },
  ];

  const dataSource = (linkableItems || []).map((x: any) => ({
    key: x.id,
    ...x,
  }));

  return (
    <AppModal
      open={open}
      title="Linked Media"
      className="memo-linked-media-modal"
      loading={false}
      centered
      onCancel={close}
      onConfirm={close}
      footer={null}
      description={
        <div style={{ marginTop: 8 }}>
          {/* Top section */}
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {/* <div>
              <div
                style={{ fontWeight: 500, marginBottom: 6, textAlign: "left" }}
              >
                <span style={{ color: "red" }}>*</span> Memo group
              </div>
              <Select
                mode="multiple"
                allowClear
                placeholder="Select group(s)"
                options={groupOptions}
                value={selectedGroupIds}
                onChange={(vals) => setSelectedGroupIds(vals)}
                style={{ width: "100%" }}
              />
            </div> */}

            <div className="description-for-memo">
              <div
                style={{ fontWeight: 500, marginBottom: 6, textAlign: "left" }}
              >
                <span style={{ color: "red" }}>*</span> Memo title
                <Input value={memo?.title || memoTitle || ""} disabled />
              </div>
              <div
                style={{ fontWeight: 500, marginBottom: 6, textAlign: "left" }}
              >
                Memo description
                <Input.TextArea
                  value={
                    memo?.content || memo?.preview || memoDescription || ""
                  }
                  disabled
                  rows={3}
                />
              </div>
            </div>
          </div>

          <div className="memo-filters-card" style={{}}>
            {/* <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}> */}
            <div
              className="linked-media-filters"
              // style={{
              //   display: "flex",
              //   flexDirection: "column",
              //   width: "100%",
              //   gap: "0.5rem",
              // }}
            >
              <span
                style={{
                  fontFamily: "Poppins",
                  textAlign: "left",
                  fontSize: "14px",
                }}
              >
                Search
              </span>
              <Search
                placeholder="Search"
                allowClear
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                //zgjidhja pa debounce
                // onChange={(e) => dispatch(setMediaUsername(e.target.value))}
                onSearch={(v) => {
                  setPage(1);
                  setSearch(v);
                  // fetch happens via effect
                }}
              />
            </div>

            <div className="linked-media-filters">
              <span
                style={{
                  fontFamily: "Poppins",
                  textAlign: "left",
                  fontSize: "14px",
                }}
              >
                Type
              </span>
              <Select
                allowClear
                placeholder="Select type"
                options={typeOptions}
                value={entityType || undefined}
                onChange={(v) => {
                  setPage(1);
                  setEntityType((v as any) || "");
                }}
                style={{ width: "100%" }}
              />
              {/* </div> */}
            </div>

            <div className="linked-media-filters">
              <span
                style={{
                  fontFamily: "Poppins",
                  textAlign: "left",
                  fontSize: "14px",
                }}
              >
                Link / Unlink
              </span>

              <Select
                allowClear
                placeholder="All"
                options={linkStatusOptions}
                value={linkStatus === "all" ? undefined : linkStatus}
                onChange={(v) => {
                  setPage(1);
                  setLinkStatus((v as LinkStatus) || "all");
                }}
                style={{ width: "100%" }}
              />
            </div>
          </div>

          {/* Table */}
          <div
            style={{
              marginTop: 14,
              overflowX: "auto",
              WebkitOverflowScrolling: "touch",
            }}
          >
            {linkableLoading ? (
              <div
                style={{
                  padding: 18,
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <Spin />
              </div>
            ) : (
              <div style={{ minWidth: 600 }}>
                <GenericTable<any>
                  columns={columns}
                  dataSource={dataSource}
                  loading={linkableLoading}
                  emptyText="No items found"
                  pagination={{
                    current: page,
                    pageSize,
                    total: linkableTotal,
                    showSizeChanger: false,
                    showQuickJumper: false,
                    onChange: (p, ps) => {
                      setPage(p);
                      if (ps) setPageSize(ps);
                    },
                  }}
                  scroll={{ y: 220 }}
                />
              </div>
            )}
          </div>
        </div>
      }
    />
  );
};

export default LinkedMediaModalForMemo;
